import React, { useMemo } from 'react';
import { Modal } from './Modal';
import { ModalHeader } from './ModalHeader';
import { ModalDownloadButton } from './ModalDownloadButton';
import { ModalFilters } from './ModalFilters';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { getScoreCellClass, getRankClass, getAvgColor } from './utils';
import { SECTIONS } from './DownloadManager';
import { useClassInsights } from './ClassInsightsContext';
import { catalogClasses } from './catalogSelectors';
import classAvgIcon from '../../static/icons/ClassAvgIcon.svg';

export function ShortAnswerModal({
  open,
  onClose,
  onExited,
  list,
  selectedIds,
  onChange,
  stats,
  isLoading,
  sortBy,
  onSortChange,
}) {
  const { filters, setFilters, catalog } = useClassInsights();
  const sortOptions = [
    'Performance - High to low',
    'Performance - Low to high',
  ];

  const { students, headers, classAverage } = useMemo(() => {
    if (!stats || !stats.tasks || stats.tasks.length === 0) {
      return { students: [], headers: [], classAverage: 0 };
    }

    const headers = stats.tasks.map((task) => {
      const questionNumbers = Array.from(new Set(
        task.studentStats.flatMap(s =>
          s.entries
            .map((entry) => Number(entry?.questionSerialNumber?.value))
            .filter((num) => Number.isFinite(num) && num > 0)
        )
      )).sort((a, b) => a - b);

      return {
        taskId: task.taskId,
        taskTitle: task.taskTitle,
        questionNumbers,
      };
    });

    const studentMap = new Map();

    stats.tasks.forEach((task) => {
      task.studentStats.forEach((studentStat) => {
        if (!studentMap.has(studentStat.studentId)) {
          studentMap.set(studentStat.studentId, {
            studentId: studentStat.studentId,
            name: studentStat.studentName,
            scoresByTask: new Map(),
            backendAvg: studentStat.average,
          });
        }

        const student = studentMap.get(studentStat.studentId);
        const scoresByQuestion = new Map();

        studentStat.entries.forEach((entry) => {
          const questionNumber = Number(entry?.questionSerialNumber?.value);
          if (!Number.isFinite(questionNumber) || questionNumber <= 0) return;
          const awardedMarks = entry.awardMarks || entry.awardedMarks;
          scoresByQuestion.set(questionNumber, `${awardedMarks}/${entry.highestMarks}`);
        });

        student.scoresByTask.set(task.taskId, scoresByQuestion);
      });
    });

    const students = Array.from(studentMap.values()).map((student, index) => {
      const allScores = [];

      stats.tasks.forEach((task) => {
        const scoresByQuestion = student.scoresByTask.get(task.taskId);
        if (!scoresByQuestion) return;
        allScores.push(...scoresByQuestion.values());
      });

      return {
        studentId: student.studentId,
        name: student.name,
        rank: index + 1,
        scores: allScores,
        scoresByTask: student.scoresByTask,
        avg: `${student.backendAvg}%`,
      };
    });

    return {
      students,
      headers,
      classAverage: stats.averages.classAverage,
    };
  }, [stats]);

  const classes = catalogClasses(catalog);

  // Create enhanced CSV with task and question information
  const csv = students.map((s) => {
    const row = {
      Rank: s.rank,
      Student: s.name,
    };

    // Add scores with task/question labels
    headers.forEach((header) => {
      const scoresByQuestion = s.scoresByTask.get(header.taskId);
      if (scoresByQuestion) {
        header.questionNumbers.forEach((qNum) => {
          const score = scoresByQuestion.get(qNum);
          const label = `${header.taskTitle} - Q${qNum}`;
          row[label] = score || '-';
        });
      }
    });

    row.Average = s.avg;
    return row;
  });

  const shortAnswerLabels = selectedIds
    .map(id => list.find(sa => String(sa.id) === String(id)))
    .filter(Boolean)
    .map(sa => sa.title || sa.name || 'Untitled Short Answer');

  const classLabels = filters.classIds
    .map(id => classes.find(c => String(c.id) === String(id)))
    .filter(Boolean)
    .map(c => c.title || c.name);

  const exportFilters = {
    filters,
    classLabels,
    shortAnswerLabels,
  };

  return (
    <Modal
      open={open}
      onExited={onExited}
      onOverlayClick={onClose}
      className="ci-modal-wide"
      data-section="short-answer-modal"
    >
      <ModalHeader title="Short Answers" onClose={onClose}>
        <ExportFormatDropdown
          section={SECTIONS.SHORT_ANSWER}
          data={stats}
          csvData={csv}
          isModal={true}
          filterData={exportFilters}
          trigger={<ModalDownloadButton />}
        />
      </ModalHeader>

      <ModalFilters
        firstFilterLabel="Short Answers"
        firstFilterOptions={list}
        firstFilterValue={selectedIds}
        firstFilterOnChange={(values) => onChange(values.map(String))}
        firstFilterGetOptionLabel={(opt) => opt.title || opt.name}
        firstFilterGetOptionValue={(opt) => String(opt.id)}
        filters={filters}
        setFilters={setFilters}
        firstFilterShowAllOption={true}
        firstFilterShowAllWhenAllSelected={true}
        className="ci-rubric-modal-filters"
      />

      {!isLoading && students.length > 0 && (
        <div className="ci-modal-table-controls">
          <div className="ci-modal-sort">
            <span>Sort by:</span>
            <DropdownMenu
              multiselect
              singleSelect
              options={sortOptions}
              selectedValues={[sortBy]}
              onChange={(values) => onSortChange(values[0] || sortOptions[0])}
              placeholder="Sort"
              getOptionLabel={(opt) => opt}
              getOptionValue={(opt) => opt}
              alwaysWhite
              compactDisplay
              showCheckboxes={false}
              showAllOption={false}
              triggerFontSize="14px"
              triggerPadding="8px 12px"
              triggerWidth="auto"
              triggerMinWidth="280px"
            />
          </div>
          <div className="ci-modal-class-avg">
            <img src={classAvgIcon} alt="" className="ci-modal-class-avg-icon" />
            Class average: <span>{classAverage}%</span>
          </div>
        </div>
      )}

      <div className="ci-modal-table-content">
        {isLoading ? (
          <div className="ci-panel-loading">
            <SmallLoader />
          </div>
        ) : students.length === 0 ? (
          <div className="ci-empty-state">No data available</div>
        ) : (
          <table className="ci-modal-table ci-short-answer-table">
            <thead>
              <tr>
                <th className="ci-sticky-col">Student</th>
                {headers.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                {headers.map((header, headerIndex) => (
                  <React.Fragment key={header.taskId}>
                    <th colSpan={Math.max(1, header.questionNumbers.length)} className="ci-task-group-header">
                      {header.taskTitle}
                    </th>
                    {headerIndex < headers.length - 1 && (
                      <th className="ci-group-separator-cell" aria-hidden="true" />
                    )}
                  </React.Fragment>
                ))}
                {headers.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                <th className="ci-sticky-right">Average</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student.studentId}>
                  <td className="ci-sticky-col">
                    <div className="ci-table-student">
                      <span className={getRankClass(student.rank)}>{student.rank}</span>
                      {student.name}
                    </div>
                  </td>
                  {headers.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                  {headers.map((header, headerIndex) => {
                    const scoresByQuestion = student.scoresByTask.get(header.taskId);
                    const questionColumns = header.questionNumbers.length > 0 ? header.questionNumbers : [null];
                    return (
                      <React.Fragment key={`${student.studentId}-${header.taskId}`}>
                        {questionColumns.map((questionNumber, questionIndex) => {
                          const score = questionNumber == null ? '-' : (scoresByQuestion?.get(questionNumber) || '-');
                          const classes = [getScoreCellClass(score), 'ci-score-cell'];
                          if (questionIndex === 0) classes.push('ci-score-cell--group-start');
                          if (questionIndex === questionColumns.length - 1) classes.push('ci-score-cell--group-end');
                          return (
                            <td
                              key={`${header.taskId}-${questionNumber ?? 'q'}`}
                              className={classes.join(' ')}
                            >
                              {score}
                            </td>
                          );
                        })}
                        {headerIndex < headers.length - 1 && (
                          <td className="ci-group-separator-cell" aria-hidden="true" />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {headers.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                  <td
                    className="ci-cell-avg ci-sticky-right"
                    style={{ backgroundColor: getAvgColor(parseInt(student.avg, 10)) }}
                  >
                    {student.avg}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
